import { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { useLocationCtx } from "../../context/LocationContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { MapPin, CheckCircle, XCircle } from "@phosphor-icons/react";

// Modal that gates a service action on serviceability for a given category.
// Props: open, onClose, category, defaultPincode, onServiceable(pincode, geo)
export function ServiceabilityGate({ open, onClose, category, defaultPincode = "", onServiceable }) {
  const { setPincode } = useLocationCtx();
  const [pin, setPin] = useState(defaultPincode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setPin(defaultPincode); setError(""); } }, [open, defaultPincode]);

  if (!open) return null;

  const check = async () => {
    if (!/^\d{6}$/.test(pin)) { setError("Enter a valid 6-digit pincode"); return; }
    setBusy(true); setError("");
    try {
      const { data } = await api.get("/public/check-service", { params: { pincode: pin, category: category || "" } });
      if (data.available) {
        try { await setPincode(pin); } catch (e) {}
        onServiceable(pin, data);
        onClose();
      } else if (!data.serviceable_area) {
        setError(`Sorry, we don't service pincode ${pin} yet. Try a different pincode.`);
      } else {
        setError(`${category || "This service"} isn't available at ${pin} (${data.city || "your area"}) yet.`);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Could not verify serviceability. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose} data-testid="serviceability-gate">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-6 pb-2">
          <div className="w-12 h-12 rounded-xl bg-[#eef2ff] flex items-center justify-center mb-3">
            <MapPin size={26} weight="fill" className="text-[#4F46E5]" />
          </div>
          <h3 className="font-[Outfit] text-xl font-extrabold tracking-tight">Check serviceability</h3>
          <p className="text-sm text-[#475569] mt-1.5">
            Enter your pincode to confirm we serve {category ? <strong>{category}</strong> : "this service"} in your area before booking.
          </p>
        </div>
        <div className="px-6 pb-6 pt-3 space-y-3">
          <Input
            data-testid="gate-pincode-input"
            value={pin}
            maxLength={6}
            inputMode="numeric"
            placeholder="Enter 6-digit pincode"
            onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
            onKeyDown={(e) => e.key === "Enter" && check()}
            className="rounded-xl h-12 text-lg tracking-wide"
          />
          {error && (
            <p className="text-sm text-[#b91c1c] flex items-start gap-1.5" data-testid="gate-error">
              <XCircle size={16} weight="fill" className="mt-0.5 shrink-0" /> {error}
            </p>
          )}
          <Button onClick={check} disabled={busy} data-testid="gate-check-btn"
            className="rounded-xl w-full h-11 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold gap-2">
            <CheckCircle size={18} weight="bold" /> {busy ? "Checking…" : "Check & Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
